import React from "react";
import { Card, ErrorMessage, Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import {
  PaymentPrefCreationForAccountWorkflow,
  PaymentPrefEditForAccountWorkflow,
  useDeletePaymentPref,
  DeletePaymentPrefModal,
  ExistingPaymentPrefDisplayCard,
  InstructionsNeededForPositionsCard,
} from "@jsq/payments-components";
import { WorkflowType } from "../../common/enums";
import { type Arena } from "../../common/types";
import { useAccountPaymentPrefController } from "./controller";
import { styleLastChild } from "./styles_vanilla.css";

export type PaymentPrefForAccountContainerProps = {
  accountId: string;
  arena?: Arena;
  banner?: React.ReactNode;
};

/**
 * A container component that manages and displays payment preferences for a specific account.
 * It handles loading states, error states, and renders different payment preference related components
 * based on the account's state.
 *
 * The component provides functionality for:
 * - Displaying existing payment preferences
 * - Creating new payment preferences
 * - Editing existing payment preferences
 * - Deleting payment preferences
 * - Showing positions that need payment instructions
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string} props.accountId - The ID of the account to display payment preferences for
 * @param {Arena} [props.arena] - Arena configuration object that determines which mutation to call based on hasPositionPaymentPrefStaffApprovals feature flag
 * @param {React.ReactNode} [props.banner] - Optional banner to display at the top of the component
 *
 * @example
 * ```tsx
 * <PaymentPrefForAccountContainer
 *   accountId="123"
 *   arena={{ id: "global", features: { hasPositionPaymentPrefStaffApprovals: true }}}
 *   banner={<CustomBanner />}
 * />
 * ```
 *
 * @remarks
 * - Uses a controller pattern with `useAccountPaymentPrefController` to manage state and logic
 * - Handles loading and error states with appropriate UI feedback
 * - Provides empty state handling when no positions exist
 * - Supports conditional rendering of edit/reveal controls based on permissions
 */
export const PaymentPrefForAccountContainer: React.FC<PaymentPrefForAccountContainerProps> = ({
  accountId,
  arena,
  banner,
}) => {
  const {
    dataLoading,
    dataError,
    accountPaymentPrefsData,
    positionsPaginated,
    handleLoadMorePositions,
    handleCreatePaymentPrefClick,
    showCreatePaymentPref,
    createPaymentPrefId,
    handleCancelCreatePaymentPref,
    handleSuccessCreatePaymentPref,
    handleEditPaymentPrefClick,
    showEditPaymentPref,
    editPaymentPrefId,
    handleCancelEditPaymentPref,
    handleSuccessEditPaymentPref,
    canEdit,
    canReveal,
    showInstructionsNeededComponent,
    refetchData,
  } = useAccountPaymentPrefController({ accountId });

  const {
    handleDeletePaymentInstructionClick,
    showDeletePaymentPrefModal,
    handleCancelDeletePaymentPref,
    deletePaymentPrefCallback,
  } = useDeletePaymentPref({
    workflowType: WorkflowType.Account,
    accountId,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSuccess: refetchData,
  });

  if (dataLoading) {
    return (
      <Card>
        <LoadingIndicator variant="primary" />
      </Card>
    );
  }

  if (dataError) {
    return (
      <Card>
        <ErrorMessage>Failed to load</ErrorMessage>
      </Card>
    );
  }

  // If there are no payment prefs and there are no positions without payment prefs that means there should be no
  // positions on the account, return empty state.
  if (
    !(accountPaymentPrefsData?.account?.paymentPrefs?.length ?? -1 > 0) &&
    !showInstructionsNeededComponent
  ) {
    return (
      <Card title="Payment instructions">
        <Text variant="regular-md" style={{ fontStyle: "italic" }}>
          No positions yet
        </Text>
      </Card>
    );
  }

  return (
    <Card title="Payment instructions">
      {banner}
      {accountPaymentPrefsData?.account?.paymentPrefs && (
        <Flex flexDirection="column" gap={20} className={styleLastChild}>
          {accountPaymentPrefsData?.account?.paymentPrefs?.map((paymentPref) => (
            <ExistingPaymentPrefDisplayCard
              key={paymentPref.id}
              paymentPref={paymentPref}
              displayPositions
              displayRevealDetails={canReveal}
              displayInvalidInstructionsWarning
              callBacks={
                canEdit
                  ? {
                      editPaymentInstructionsCallback: () => {
                        handleEditPaymentPrefClick(paymentPref?.id);
                      },
                      switchPaymentMethodCallback: () => {
                        handleCreatePaymentPrefClick(paymentPref?.id);
                      },
                      deletePaymentInstructionsCallback: () =>
                        handleDeletePaymentInstructionClick(paymentPref?.id),
                    }
                  : undefined
              }
            />
          ))}
        </Flex>
      )}

      {showInstructionsNeededComponent ? (
        <InstructionsNeededForPositionsCard
          positionsPaginated={positionsPaginated}
          positionListLoading={dataLoading}
          positionListError={dataError}
          fetchMorePositions={handleLoadMorePositions}
          showAddPayment={canEdit}
          handleCreatePayment={() => handleCreatePaymentPrefClick("")}
        />
      ) : null}
      <DeletePaymentPrefModal
        show={showDeletePaymentPrefModal}
        onClose={handleCancelDeletePaymentPref}
        deleteCallback={deletePaymentPrefCallback}
      />
      {showCreatePaymentPref && (
        <PaymentPrefCreationForAccountWorkflow
          accountId={accountId}
          onExit={handleCancelCreatePaymentPref}
          onSuccess={() => void handleSuccessCreatePaymentPref()}
          sourcePaymentPrefId={createPaymentPrefId}
          arena={arena}
        />
      )}
      {showEditPaymentPref && (
        <PaymentPrefEditForAccountWorkflow
          accountId={accountId}
          onExit={handleCancelEditPaymentPref}
          onSuccess={() => void handleSuccessEditPaymentPref()}
          sourcePaymentPrefId={editPaymentPrefId}
          arena={arena}
        />
      )}
    </Card>
  );
};
